import * as _ from 'lodash';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';

import { ApiModel } from './api.model';
import { CrmServiceModel } from './crm-service.model';
import { Organization, User } from 'services/authenticatedUser/types';
import { NotificationRequestBody, NotificationRequestData, RelationRetailer } from 'models/brand/types';
import { deburrAndLower } from 'helpers/sanitation';
import { retailerRoles, manufacturerRoles } from 'helpers/team-user-roles';
import { stringify } from 'query-string';

@Injectable()
export class OrganizationModel {
    constructor(
        private apiModel: ApiModel,
        private crmServiceModel: CrmServiceModel
    ) {}

    public getOrganizationInvitedUsers(organizationId: string): Observable<OrganizationInvitedUser[]> {
        return this.crmServiceModel
            .makeHttpObservableRequest<{ data: OrganizationInvitedUser[] }>({
                url: this.crmServiceModel.getOrganizationInvitedUsersEndpoint(organizationId),
                method: 'GET'
            })
            .pipe(map((response) => response.data ?? []));
    }

    public createBranch(organizationId: string, branch: BranchToCreate): Promise<any> {
        return this.apiModel.makeHttpRequest({
            url: `${this.apiModel.getOrganizationsEndpoint()}/${organizationId}/branches`,
            method: 'post',
            body: branch
        });
    }

    public putBranch(organizationId: string, gln: string, branch: BranchToCreate): Promise<any> {
        return this.apiModel.makeHttpRequest({
            url: `${this.apiModel.getOrganizationsEndpoint()}/${organizationId}/branches/${gln}`,
            method: 'put',
            body: branch
        });
    }

    public patchBranchSettings(organizationId: string, gln: string, branch: BranchSettings): Promise<any> {
        return this.apiModel.makeHttpRequest({
            url: `${this.apiModel.getOrganizationsEndpoint()}/${organizationId}/branches/${gln}/settings`,
            method: 'patch',
            body: branch
        });
    }

    public createBranchUser(organizationId: string, gln: string, user: any): Promise<any> {
        return this.apiModel.makeHttpRequest({
            url: `${this.apiModel.getOrganizationsEndpoint()}/${organizationId}/branches/${gln}/users`,
            method: 'post',
            body: user
        });
    }

    public deleteBranchUser(organizationId: string, gln: string, username: string): Promise<any> {
        return this.apiModel.makeHttpRequest({
            url: `${this.apiModel.getOrganizationsEndpoint()}/${organizationId}/branches/${gln}/users/${username}`,
            method: 'delete'
        });
    }

    public getBranches(organizationId: string, params?: any): Promise<Branch[]> {
        return this.apiModel.makeHttpRequest({
            url: `${this.apiModel.getOrganizationsEndpoint()}/${organizationId}/branches`,
            method: 'get',
            params
        });
    }

    public getBranchesByGln(gln): Promise<Branch[]> {
        return this.apiModel.makeHttpRequest({
            url: `${this.apiModel.getOrganizationsEndpoint()}/branches/signup?gln=${gln}`,
            method: 'get'
        });
    }

    public getBranchesAsObservable(organizationId: string): Observable<Branch[]> {
        return this.apiModel.makeHttpObservableRequest({
            url: `${this.apiModel.getOrganizationsEndpoint()}/${organizationId}/branches`,
            method: 'get'
        });
    }

    public getAllBranches(params: any): Promise<any[]> {
        return this.apiModel.makeHttpRequest({
            url: `${this.apiModel.getOrganizationsEndpoint()}/branches`,
            method: 'get',
            params
        });
    }

    public getBranchByGln(organizationId: string, gln: string) {
        return this.apiModel.makeHttpRequest<Branch>({
            url: `${this.apiModel.getOrganizationsEndpoint()}/${organizationId}/branches/${gln}`,
            method: 'get'
        });
    }

    public deleteBranchByGln(organizationId: string, gln: string) {
        return this.apiModel.makeHttpRequest<Branch>({
            url: `${this.apiModel.getOrganizationsEndpoint()}/${organizationId}/branches/${gln}`,
            method: 'delete'
        });
    }

    public getOrganizationStorages(brandId: string) {
        return this.apiModel.makeHttpObservableRequest({
            url: `${this.apiModel.getOrganizationsEndpoint()}/storage?brandId=${brandId}`,
            method: 'get'
        });
    }

    public getRetailers(): Observable<RetailerForOrder[]> {
        return this.apiModel
            .makeHttpObservableRequest({
                url: `${this.apiModel.getOrganizationsEndpoint()}/?type=retailer`,
                method: 'get'
            })
            .pipe(sortRetailerByName);
    }

    public getOrganization(organizationId: string): Promise<RetailerForOrder> {
        return this.apiModel.makeHttpRequest({
            url: `${this.apiModel.getOrganizationsEndpoint()}/${organizationId}`,
            method: 'get'
        });
    }

    public getRetailerUsersForPreorder(organizationId: string): Observable<RetailerUser[]> {
        const queryParams = { privileges: ['admin', 'buyer'] };
        return this.apiModel.makeHttpObservableRequest({
            url: `${this.apiModel.getOrganizationsEndpoint()}/${organizationId}/users?${stringify(queryParams, {
                arrayFormat: 'bracket',
                sort: false
            })}`,
            method: 'get'
        });
    }

    public getRetailersWithReorderAccess(organizationId: string): Observable<RetailerForOrder[]> {
        return this.apiModel
            .makeHttpObservableRequest({
                url: `${this.apiModel.getOrganizationsEndpoint()}/${organizationId}/retailers`,
                method: 'get'
            })
            .pipe(sortRetailerByName);
    }

    public addBrandsNotifications(organizationId: string, request: NotificationRequestBody): Promise<any> {
        return this.apiModel.makeHttpRequest({
            url: `${this.apiModel.getOrganizationsEndpoint()}/${organizationId}/notifications`,
            method: 'post',
            body: request
        });
    }

    public removeBrandsNotifications(organizationId: string, request: NotificationRequestData[]): Promise<any> {
        return this.apiModel.makeHttpRequest({
            url: `${this.apiModel.getOrganizationsEndpoint()}/${organizationId}/notifications`,
            method: 'delete',
            body: request
        });
    }

    public putBrandsNotifications(organizationId: string, request: NotificationRequestBody): Promise<any> {
        return this.apiModel.makeHttpRequest({
            url: `${this.apiModel.getOrganizationsEndpoint()}/${organizationId}/notifications`,
            method: 'put',
            body: request
        });
    }

    public getRetailerRoleByPrivileges(privileges: string[]): UserRole {
        return this.getRoleByPrivileges(retailerRoles, privileges);
    }

    public getManufacturerRoleByPrivileges(privileges: string[]): UserRole {
        return this.getRoleByPrivileges(manufacturerRoles, privileges);
    }

    public pullRelationRetailers(brandId?: string[]) {
        return this.apiModel.makeHttpObservableRequest<RelationRetailer[]>({
            method: 'GET',
            url: `${this.apiModel.getAccessEndpoint()}/relations/retailers`,
            params: brandId ? { brandId } : {}
        });
    }

    private getRoleByPrivileges(roles: UserRole[], privileges: string[]): UserRole {
        return _.first(
            _.filter(
                roles,
                (role) =>
                    _.isEmpty(_.difference(role.privileges, privileges)) &&
                    _.isEqual(_.size(role.privileges), _.size(privileges))
            )
        );
    }
}

const sortRetailerByName = map<RetailerForOrder[], RetailerForOrder[]>((retailers) =>
    _.sortBy(retailers, ({ name }) => deburrAndLower(name))
);

interface UserRole {
    name: string;
    title: string;
    description: string;
    privileges: string[];
}

export enum AccessRight {
    OrderViaB2bPlatform = 'canOrderViaB2bPlatform',
    OrderViaPreorderPlatform = 'canOrderViaPreorderPlatform',
    MarketingFiles = 'canAccessMarketingFiles',
    ProductData = 'canAccessProductData'
}

export type RetailerForOrder = Pick<Organization, '_id' | 'name' | 'address'> & {
    branches: Branch[];
    users: RetailerUser[];
};

export type RetailerUser = Pick<User, 'firstname' | 'surname' | 'email' | 'privileges' | '_id'>;

export interface Branch {
    _id?: string;
    address: {
        _id?: string;
        address1: string;
        city: string;
        country: string;
        gln: string;
        fcGln?: string;
        zip: string;
    };
    contacts?: { email: string }[];
    phone: string;
    name?: string;
    shortName?: string;
    buyerAddress?: { gln: string };
    billingAddress?: { gln: string };
    users: BranchUser[];
    locationServiceId: string;
    isRestrictedFromEdit: boolean;
    isEnabled?: boolean;
}

interface BranchUser {
    username: string;
    password?: string;
    fullName: string;
    firstLogin?: Date;
    lastLogin?: Date;
    created?: Date;
}

export type BranchToCreate = Omit<Branch, 'users' | 'isRestrictedFromEdit' | 'locationServiceId'> & {
    useFcGln: boolean;
    isSelected?: boolean;
};

export interface BranchSettings {
    isEnabled: boolean;
}

export interface OrganizationInvitedUser {
    _id: string;
    inviteeEmail: string;
    created: Date;
    updated: Date;
}
